import type { I18n, Step, StepId } from './types'

export const START_STEP: StepId = 'start'
export const END_STEP: StepId = 'END'

const t = (es: string, en: string): I18n => ({ es, en })

export const FLOWS: Record<StepId, Step> = {
  start: {
    id: 'start',
    kind: 'options',
    text: t(
      '¡Hola! 👋 Bienvenid@ a Person Inmobiliaria 🏡 ¿En qué podemos ayudarte?',
      'Hi! 👋 Welcome to Person Inmobiliaria 🏡 How can we help you?'
    ),
    saveAs: 'intencion',
    options: [
      { value: 'comprar', label: t('Quiero comprar', 'I want to buy'), next: 'buy_has_ref' },
      { value: 'alquilar', label: t('Quiero alquilar', 'I want to rent'), next: 'rent_province' },
      {
        value: 'propietario',
        label: t('Soy propietario', "I'm an owner"),
        next: 'owner_intention',
      },
      { value: 'otros', label: t('Otros', 'Other'), next: 'other_reason' },
    ],
  },

  // ─── BUY ───────────────────────────────────────────────────────────
  buy_has_ref: {
    id: 'buy_has_ref',
    kind: 'options',
    text: t(
      "¿Tienes la referencia de la propiedad? (empieza por 'P')",
      "Do you have the property reference? (starts with 'P')"
    ),
    options: [
      { value: 'si', label: t('Sí', 'Yes'), next: 'buy_ref_input' },
      { value: 'no', label: t('No', 'No'), next: 'buy_province' },
    ],
  },
  buy_ref_input: {
    id: 'buy_ref_input',
    kind: 'input',
    text: t('Introduce la referencia (ej. P1234)', 'Enter the reference (e.g. P1234)'),
    saveAs: 'ref_code',
    inputType: 'ref',
    placeholder: t('P1234', 'P1234'),
    next: 'lead_buy_ref',
  },
  buy_province: {
    id: 'buy_province',
    kind: 'options',
    text: t('¿En qué provincia buscas?', 'Which province?'),
    saveAs: 'provincia',
    options: [
      { value: 'valencia', label: t('Valencia', 'Valencia'), next: 'buy_type' },
      { value: 'castellon', label: t('Castellón', 'Castellón'), next: 'buy_type' },
      { value: 'alicante', label: t('Alicante', 'Alicante'), next: 'buy_type' },
    ],
  },
  buy_type: {
    id: 'buy_type',
    kind: 'options',
    text: t('¿Qué tipo de propiedad?', 'What type of property?'),
    saveAs: 'tipo_propiedad',
    options: [
      { value: 'piso', label: t('Piso', 'Apartment'), next: 'buy_budget' },
      { value: 'chalet', label: t('Chalet', 'House / Chalet'), next: 'buy_budget' },
      { value: 'local', label: t('Local', 'Commercial'), next: 'buy_budget' },
      { value: 'garaje', label: t('Garaje', 'Garage'), next: 'buy_budget' },
      { value: 'otro', label: t('Otro', 'Other'), next: 'buy_budget' },
    ],
  },
  buy_budget: {
    id: 'buy_budget',
    kind: 'options',
    text: t('¿Cuál es tu presupuesto?', "What's your budget?"),
    saveAs: 'presupuesto',
    options: [
      { value: 'lt_200000', label: t('Menos de 200.000 €', 'Less than €200,000'), next: 'lead_buy_search' },
      { value: '200000_400000', label: t('200.000 – 400.000 €', '€200,000 – €400,000'), next: 'lead_buy_search' },
      { value: '400000_600000', label: t('400.000 – 600.000 €', '€400,000 – €600,000'), next: 'lead_buy_search' },
      { value: '600000_800000', label: t('600.000 – 800.000 €', '€600,000 – €800,000'), next: 'lead_buy_search' },
      { value: 'gt_800000', label: t('Más de 800.000 €', 'More than €800,000'), next: 'lead_buy_search' },
    ],
  },
  // Only this branch carries a reference, so it's the only buy branch that may
  // file as 'listing' — in the CRM that type means "one concrete property".
  lead_buy_ref: {
    id: 'lead_buy_ref',
    kind: 'leadForm',
    text: t(
      'Déjanos tus datos y un agente se pondrá en contacto contigo.',
      'Leave your details and an agent will get in touch.'
    ),
    fields: ['name', 'email', 'phone', 'rgpd'],
    submitType: 'listing',
    next: 'END',
  },
  // The buyer described what they're looking for: no property behind it yet.
  lead_buy_search: {
    id: 'lead_buy_search',
    kind: 'leadForm',
    text: t(
      'Déjanos tus datos y un agente se pondrá en contacto contigo.',
      'Leave your details and an agent will get in touch.'
    ),
    fields: ['name', 'email', 'phone', 'rgpd'],
    submitType: 'contact',
    next: 'END',
  },

  // ─── RENT ──────────────────────────────────────────────────────────
  rent_province: {
    id: 'rent_province',
    kind: 'options',
    text: t('¿En qué provincia buscas?', 'Which province?'),
    saveAs: 'provincia',
    options: [
      { value: 'valencia', label: t('Valencia', 'Valencia'), next: 'rent_type' },
      { value: 'castellon', label: t('Castellón', 'Castellón'), next: 'rent_type' },
      { value: 'alicante', label: t('Alicante', 'Alicante'), next: 'rent_type' },
    ],
  },
  rent_type: {
    id: 'rent_type',
    kind: 'options',
    text: t('¿Qué tipo de propiedad?', 'What type of property?'),
    saveAs: 'tipo_propiedad',
    options: [
      { value: 'piso', label: t('Piso', 'Apartment'), next: 'rent_budget' },
      { value: 'chalet', label: t('Chalet', 'House / Chalet'), next: 'rent_budget' },
      { value: 'local', label: t('Local', 'Commercial'), next: 'rent_budget' },
      { value: 'garaje', label: t('Garaje', 'Garage'), next: 'rent_budget' },
      { value: 'otro', label: t('Otro', 'Other'), next: 'rent_budget' },
    ],
  },
  rent_budget: {
    id: 'rent_budget',
    kind: 'options',
    text: t('¿Cuál es tu presupuesto mensual?', "What's your monthly budget?"),
    saveAs: 'presupuesto_alquiler',
    options: [
      { value: 'lt_1000', label: t('Menos de 1.000 €/mes', 'Less than €1,000/month'), next: 'lead_rent' },
      { value: '1000_1500', label: t('1.000 – 1.500 €/mes', '€1,000 – €1,500/month'), next: 'lead_rent' },
      { value: '1500_2000', label: t('1.500 – 2.000 €/mes', '€1,500 – €2,000/month'), next: 'lead_rent' },
      { value: '2000_4000', label: t('2.000 – 4.000 €/mes', '€2,000 – €4,000/month'), next: 'lead_rent' },
      { value: 'gt_4000', label: t('Más de 4.000 €/mes', 'More than €4,000/month'), next: 'lead_rent' },
    ],
  },
  // A rent search never carries a reference: it files as 'contact'.
  lead_rent: {
    id: 'lead_rent',
    kind: 'leadForm',
    text: t(
      'Déjanos tus datos y un agente se pondrá en contacto contigo.',
      'Leave your details and an agent will get in touch.'
    ),
    fields: ['name', 'email', 'phone', 'rgpd'],
    submitType: 'contact',
    next: 'END',
  },

  // ─── OWNER (valuation) ─────────────────────────────────────────────
  owner_intention: {
    id: 'owner_intention',
    kind: 'options',
    text: t('¿Qué quieres hacer con tu propiedad?', 'What would you like to do with your property?'),
    saveAs: 'intention',
    options: [
      { value: 'vender', label: t('Vender', 'Sell'), next: 'owner_property_type' },
      { value: 'alquilar', label: t('Alquilar', 'Rent it out'), next: 'owner_property_type' },
    ],
  },
  owner_property_type: {
    id: 'owner_property_type',
    kind: 'options',
    text: t('¿Qué tipo de vivienda es?', 'What type of property is it?'),
    saveAs: 'property_type',
    options: [
      { value: 'piso', label: t('Piso', 'Apartment'), next: 'owner_location' },
      { value: 'chalet', label: t('Chalet', 'House / Chalet'), next: 'owner_location' },
      { value: 'local', label: t('Local', 'Commercial'), next: 'owner_location' },
      { value: 'garaje', label: t('Garaje', 'Garage'), next: 'owner_location' },
      { value: 'otro', label: t('Otro', 'Other'), next: 'owner_location' },
    ],
  },
  owner_location: {
    id: 'owner_location',
    kind: 'input',
    text: t('¿En qué zona o ciudad está?', 'In which area or city?'),
    saveAs: 'property_location',
    inputType: 'string',
    placeholder: t('Ej. Ruzafa, Valencia', 'e.g. Ruzafa, Valencia'),
    next: 'owner_sqmts',
  },
  owner_sqmts: {
    id: 'owner_sqmts',
    kind: 'input',
    text: t('¿Cuántos metros cuadrados tiene?', 'How many square meters?'),
    saveAs: 'property_sqmts',
    inputType: 'number',
    placeholder: t('Ej. 90', 'e.g. 90'),
    next: 'owner_estado',
  },
  owner_estado: {
    id: 'owner_estado',
    kind: 'options',
    text: t('¿En qué estado se encuentra?', 'What condition is it in?'),
    saveAs: 'estado',
    options: [
      { value: 'nuevo', label: t('Nuevo / a estrenar', 'New / brand new'), next: 'lead_owner' },
      { value: 'reformado', label: t('Reformado', 'Recently renovated'), next: 'lead_owner' },
      { value: 'a_reformar', label: t('A reformar', 'Needs renovation'), next: 'lead_owner' },
    ],
  },
  lead_owner: {
    id: 'lead_owner',
    kind: 'leadForm',
    text: t(
      'Déjanos tus datos y te haremos una valoración gratuita.',
      "Leave your details and we'll send you a free valuation."
    ),
    fields: ['name', 'phone', 'email', 'rgpd'],
    submitType: 'valuation',
    next: 'END',
  },

  // ─── OTHER ─────────────────────────────────────────────────────────
  other_reason: {
    id: 'other_reason',
    kind: 'options',
    text: t('¿Sobre qué te gustaría hablar?', 'What would you like to talk about?'),
    saveAs: 'motivo',
    options: [
      { value: 'agente', label: t('Hablar con un agente', 'Talk to an agent'), next: 'lead_agent' },
      { value: 'admin', label: t('Consulta administrativa', 'Admin question'), next: 'admin_query' },
      { value: 'publicidad', label: t('Publicidad / colaboración', 'Advertising / partnership'), next: 'ad_query' },
      { value: 'trabajo', label: t('Trabajar con nosotros', 'Work with us'), next: 'job_redirect' },
    ],
  },
  lead_agent: {
    id: 'lead_agent',
    kind: 'leadForm',
    text: t('Déjanos tus datos y un agente te llamará.', "Leave your details and an agent will call you."),
    fields: ['name', 'email', 'phone', 'rgpd'],
    submitType: 'contact',
    next: 'END',
  },
  admin_query: {
    id: 'admin_query',
    kind: 'input',
    text: t('Cuéntanos tu consulta', 'Tell us your question'),
    saveAs: 'consulta',
    inputType: 'string',
    placeholder: t('Escribe tu consulta…', 'Type your question…'),
    next: 'lead_admin',
  },
  lead_admin: {
    id: 'lead_admin',
    kind: 'leadForm',
    text: t('Déjanos tus datos para responderte.', 'Leave your details so we can get back to you.'),
    fields: ['name', 'email', 'phone', 'rgpd'],
    submitType: 'contact',
    next: 'END',
  },
  ad_query: {
    id: 'ad_query',
    kind: 'input',
    text: t('Cuéntanos brevemente tu propuesta', 'Briefly tell us about your proposal'),
    saveAs: 'idea',
    inputType: 'string',
    placeholder: t('Escribe tu propuesta…', 'Type your proposal…'),
    next: 'lead_ad',
  },
  lead_ad: {
    id: 'lead_ad',
    kind: 'leadForm',
    text: t('Déjanos tus datos.', 'Leave your details.'),
    fields: ['name', 'email', 'phone', 'rgpd'],
    submitType: 'contact',
    next: 'END',
  },
  job_redirect: {
    id: 'job_redirect',
    kind: 'redirect',
    text: t(
      'Echa un vistazo a nuestras ofertas de empleo.',
      'Take a look at our open positions.'
    ),
    buttonLabel: t('Ver ofertas', 'See openings'),
    url: '/empleo',
  },

  END: {
    id: 'END',
    kind: 'end',
    text: t(
      '¡Gracias! Hemos recibido tu mensaje y te contactaremos pronto. ✨',
      "Thanks! We've received your message and will get back to you soon. ✨"
    ),
  },
}
